/**
 * 라우터 가드 — 인증 + 롤 검증.
 * meta.roles 가 명시된 페이지는 해당 롤 보유 시만 진입.
 */
import type { NavigationGuardWithThis, RouteLocationNormalized } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { pageKeyForRoute, isAllowedForAgentAdmin, isPersonalPage } from '@/lib/pagePermissionMap';

interface RouteMeta {
  requireAuth?: boolean;
  roles?: string[];
}

function collectMeta(to: RouteLocationNormalized): RouteMeta {
  // matched 배열을 따라 inherited meta 병합 (children 라우트 지원)
  const merged: RouteMeta = {};
  for (const m of to.matched) {
    const meta = m.meta as RouteMeta;
    if (meta.requireAuth !== undefined) merged.requireAuth = meta.requireAuth;
    if (meta.roles) merged.roles = meta.roles;
  }
  return merged;
}

export const authGuard: NavigationGuardWithThis<undefined> = async (
  to: RouteLocationNormalized,
) => {
  const auth = useAuthStore();
  // 가드에서 예외가 새어 나가면 vue-router 가 이동을 조용히 취소한다 — 사용자에겐
  // '클릭이 안 먹는' 화면이 된다. initialize() 는 이미 자체적으로 삼키지만,
  // 여기서도 한 번 더 막아 어떤 실패도 '미인증'으로 수렴시킨다.
  try {
    await auth.initialize();
  } catch {
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  const meta = collectMeta(to);

  if (meta.requireAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  // 제한 agent_admin(다른 스태프역할 없음): 허용 prefix 안에서만, 그 안에서도
  // 페이지 권한이 있어야 한다.
  //
  // 예전에는 prefix 만 보고 여기서 return 해 아래 canShow 게이트를 건너뛰었다.
  // 그래서 허용 prefix 밑에 새 화면이 생길 때마다 권한 없이 열렸다 — 화면은
  // 열리는데 API 는 requirePage 로 403 이라 빈 화면만 보이는 상태였다.
  // prefix 는 바깥 울타리고, 페이지 권한이 안쪽 자물쇠다. 둘 다 통과해야 한다.
  if (auth.isRestrictedAgentAdmin) {
    if (!meta.requireAuth) return true;
    if (to.path === '/' || to.path === '/dashboard') return { path: '/applicants' };
    if (!isAllowedForAgentAdmin(to.path)) return { name: 'forbidden' };
  }

  // 바깥 변호사: 루트·대시보드는 케이스 화면으로 보낸다.
  //
  // 루트 라우트가 무조건 /dashboard 로 redirect 하는데 이 역할에는 대시보드
  // 권한이 없어서, 로그인하면 첫 화면이 403 이었다. agent_admin 은 바로 위에
  // 같은 처리가 있는데 변호사에는 빠져 있었다.
  //
  // 여기서 return 하지 않고 아래 canShow 게이트까지 태운다 — prefix 는 울타리고
  // 페이지 권한이 자물쇠라는 같은 이유다.
  if (auth.isExternalAttorney && meta.requireAuth
      && (to.path === '/' || to.path === '/dashboard')) {
    return { path: '/cases' };
  }

  // super admin 은 role 목록을 건너뛴다. 아래 canShowPage 도 같은 예외를 두고
  // 있어(잠금 방지 break-glass) 여기만 빠져 있으면 의도가 반쪽이 된다 — 실제로
  // 루트 라우트를 비롯한 일부 meta.roles 에 'Super Admin' 이 없어, 다른 역할을
  // 겸하지 않은 super admin 은 콘솔 전체가 403 이었다.
  if (meta.roles && meta.roles.length > 0 && !auth.isSuperAdmin && !auth.hasRole(meta.roles)) {
    return { name: 'forbidden' };
  }

  // 페이지 권한(canShow) 게이트 — 라우트의 레거시 pageKey 로 판정.
  // canShowPage: 매핑없는(신규) 페이지·super admin 롤은 통과. 그 외 미허용 → forbidden.
  // (직접 URL 접근 차단. /dashboard 등 매핑없는 라우트는 통과되어 잠금 방지.)
  if (meta.requireAuth && !isPersonalPage(to.path)
      && !auth.canShowPage(pageKeyForRoute(to.path))) {
    return { name: 'forbidden' };
  }

  return true;
};
